import { config, z } from 'zod'

/**
 * zod, with the JIT compiler switched off — import `z` from here rather than from `zod`
 * anywhere under `app/` (enforced by `noRestrictedImports` in biome.jsonc).
 *
 * zod decides whether to compile a fast path for object parsing by feature-detecting the
 * Function constructor: `allowsEval` in zod/v4/core/util.js runs `new Function("")` inside a
 * try/catch. Under our CSP (app/content-security-policy.ts), which allows no `'unsafe-eval'`,
 * that probe throws — zod catches it and falls back to its interpreted path, so nothing breaks,
 * but the attempt still counts as a `script-src` violation. The browser therefore fired one
 * `blocked-uri: eval` report per page load, which is noise that buries the reports that matter.
 * Verified in a browser before this existed; see https://github.com/colinhacks/zod/issues/5789.
 *
 * `jitless: true` short-circuits the probe rather than suppressing the report:
 * `fastEnabled = jit && allowsEval.value` in zod/v4/core/schemas.js never evaluates the right
 * operand, so `new Function` is never called and there is nothing to report.
 *
 * Two reasons this has to be a module every schema imports, rather than a call in
 * `app/entry.client.tsx`:
 *
 *  1. `fastEnabled` is read when a schema is CONSTRUCTED, not when it parses, and our schemas
 *     are module-level constants. The config has to be set before those modules evaluate.
 *  2. React Router's hydration script imports the route modules STATICALLY and the client entry
 *     with a dynamic `import()` in the body (`lib/dom/ssr/components.js`), so every route module
 *     — and everything it imports — evaluates strictly before `entry.client.tsx` runs. An entry
 *     that configured zod would always be too late.
 *
 * Routing it through the dependency graph instead makes the ordering a property of the imports
 * rather than something a future edit can silently break.
 *
 * Applied unconditionally rather than only in the browser. The interpreted path costs nothing
 * here: the only schemas are a stored picked location and a geo override, both small objects
 * parsed once, and no server-side hot path parses with zod at all (`app/mocks` is dev and CI
 * only). If a large response payload is ever validated server-side, split this into a
 * browser-only config rather than reaching for `'unsafe-eval'`.
 */
config({ jitless: true })

export { z }
